// frontend/src/utils/errorHandler.js
export const ErrorTypes = {
  SERVER_OVERLOADED: 'server_overloaded',
  INTERNAL_ERROR: 'internal_error',
  NETWORK_ERROR: 'network_error',
  RATE_LIMITED: 'rate_limited',
  API_KEY_INVALID: 'api_key_invalid',
  QUOTA_EXCEEDED: 'quota_exceeded',
  PERMISSION_DENIED: 'permission_denied',
  REQUEST_TOO_LARGE: 'request_too_large',
  CONTEXT_LENGTH_EXCEEDED: 'context_length_exceeded',
  INVALID_REQUEST: 'invalid_request',
  MODEL_REFUSAL: 'model_refusal',
  UNKNOWN_ERROR: 'unknown_error'
};

export const ErrorCategories = {
  RETRYABLE: 'retryable',
  RATE_LIMIT: 'rate_limit',
  AUTH_ISSUE: 'auth_issue',
  CLIENT_ERROR: 'client_error',
  UNKNOWN: 'unknown'
};

/**
 * Convert backend error details into frontend display information
 * Provider-agnostic - only cares about universal error types
 */
export const getErrorDisplayInfo = (errorDetail) => {
  if (!errorDetail || !errorDetail.error_type) {
    return {
      title: 'An error occurred',
      message: 'Please try again.',
      showRetry: false,
      variant: 'error',
      icon: '❌'
    };
  }

  const { error_type, category, message, provider, retry_able } = errorDetail;

  switch (error_type) {
    case ErrorTypes.SERVER_OVERLOADED:
      return {
        title: 'AI servers are overloaded',
        message: 'The AI service is experiencing high traffic. Please retry your message.',
        showRetry: true,
        variant: 'warning',
        icon: '🔄',
        buttonText: 'Retry'
      };

    case ErrorTypes.INTERNAL_ERROR:
      return {
        title: 'AI service error',
        message: 'The AI service encountered an internal error. Please retry.',
        showRetry: true,
        variant: 'warning',
        icon: '⚠️',
        buttonText: 'Retry'
      };

    case ErrorTypes.NETWORK_ERROR:
      return {
        title: 'Connection error',
        message: 'Failed to connect to the AI service. Please check your connection and retry.',
        showRetry: true,
        variant: 'warning',
        icon: '🌐',
        buttonText: 'Retry'
      };

    case ErrorTypes.RATE_LIMITED:
      return {
        title: 'Rate limit reached',
        message: 'Please wait a moment before sending another message.',
        showRetry: true,
        variant: 'warning',
        icon: '⏳',
        buttonText: 'Retry'
      };

    case ErrorTypes.QUOTA_EXCEEDED:
      return {
        title: 'Usage limit exceeded',
        message: 'API usage has exceeded current limits. Contact your administrator to increase quotas.',
        showRetry: false,
        variant: 'error',
        showModal: true,
        icon: '💳',
        modalTitle: 'API Quota Exceeded',
        modalMessage: 'Your API usage has exceeded the current plan limits. Please contact your administrator to upgrade your plan or increase quotas.'
      };

    case ErrorTypes.API_KEY_INVALID:
      return {
        title: 'Authentication error',
        message: 'API authentication failed. Contact your administrator.',
        showRetry: false,
        variant: 'error',
        showModal: true,
        icon: '🔐',
        modalTitle: 'Authentication Error',
        modalMessage: 'The API key is invalid or expired. Please contact your administrator to resolve this issue.'
      };

    case ErrorTypes.CONTEXT_LENGTH_EXCEEDED:
      return {
        title: 'Conversation too long',
        message: 'This conversation has exceeded the maximum length for the selected model. Start a new conversation to continue.',
        showRetry: false,
        variant: 'warning',
        icon: '📏',
        showModal: false  // or true if you want modal emphasis
      };

    case ErrorTypes.REQUEST_TOO_LARGE:
      return {
        title: 'Message too large',
        message: 'Your message is too long. Please reduce the length and try again.',
        showRetry: false,
        variant: 'error',
        icon: '📏'
      };

    case ErrorTypes.INVALID_REQUEST:
      return {
        title: 'Invalid request',
        message: 'There was an issue with your request format. Please try again.',
        showRetry: false,
        variant: 'error',
        icon: '❌'
      };

    case ErrorTypes.MODEL_REFUSAL:
      // The model declined (stop_reason "refusal"). The backend composes the
      // sentence (explanation + category when it has them); re-sending the same
      // prompt is declined again, so no Retry. (ai-chat#3)
      return {
        title: 'Request declined',
        message: message || 'The model declined this request. Try rephrasing your message.',
        showRetry: false,
        variant: 'warning',
        icon: '🚫'
      };

    case ErrorTypes.UNKNOWN_ERROR:
    default:
      return {
        title: 'Unexpected error',
        message: message || 'An unexpected error occurred. Please try again.',
        showRetry: retry_able || false,
        variant: 'error',
        icon: '❌',
        buttonText: retry_able ? 'Retry' : undefined
      };
  }
};

/**
 * Determine if an error should be handled with retry logic
 */
export const isRetryableError = (errorDetail) => {
  return errorDetail && errorDetail.retry_able === true;
};

/**
 * Determine if an error should show a modal instead of toast
 */
export const shouldShowModal = (errorDetail) => {
  const errorInfo = getErrorDisplayInfo(errorDetail);
  return errorInfo.showModal || false;
};